import { Component } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { chain } from "lodash"
import { Settings } from "@mui/icons-material"
import { Paper, IconButton } from "@mui/material"
import CompareFrontierTables from "components/compare-frontier-tables"
import LineTarget from "components/charts/line-target"
import { LOADING, OK, UNKNOWN } from "helpers/data-statuses"
import LoadingWrapper from "components/loading-wrapper"
import Loading from "components/material/loading"
import Error from "components/material/error"
import TooltipContent from "./components/tooltip-content"
import OptimizationSettings from "modules/optimization-settings"
import "./style.scss"

class CompareFrontier extends Component {
  static propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      noData: PropTypes.bool,
      isUnmodeled: PropTypes.bool,
    })).isRequired,
    detailsItems: PropTypes.object.isRequired,
    detailsData: PropTypes.array.isRequired,
    setGroup: PropTypes.func.isRequired,
    saveGroupToStore: PropTypes.func.isRequired,
    selectedGroup: PropTypes.object.isRequired,
    groups: PropTypes.array.isRequired,
    frontierChart: PropTypes.func.isRequired,
    data: PropTypes.array.isRequired,
    dots: PropTypes.array.isRequired,
    trackPanelTrigger: PropTypes.func.isRequired,
    frontierDate: PropTypes.string,
    status: PropTypes.string.isRequired,
    comparisonItemsStatus: PropTypes.string,
    comparisonItemLoadingSuccess: PropTypes.func.isRequired,
    chartLegendFocus: PropTypes.func.isRequired,
    hoveredLegendItem: PropTypes.object,
    toggleFrontierSettings: PropTypes.func.isRequired,
    frontierSettingsPanelOpen: PropTypes.bool.isRequired,
    resetSettings: PropTypes.func.isRequired,
    setSettings: PropTypes.func.isRequired,
    setConstraints: PropTypes.func.isRequired,
    removeFactor: PropTypes.func.isRequired,
    currentConstraints: PropTypes.object.isRequired,
    portfolioData: PropTypes.object,
    portfolioType: PropTypes.string.isRequired,
    reloadChart: PropTypes.bool.isRequired,
    selectedCustomForecast: PropTypes.string,
    portfolioNetLong: PropTypes.number.isRequired,
    setConstraint: PropTypes.func.isRequired,
    setSetting: PropTypes.func.isRequired,
    activatedConstraints: PropTypes.object.isRequired,
    setFactors: PropTypes.func.isRequired,
    removeActivatedFactor: PropTypes.func.isRequired,
    currency: PropTypes.string,
  }

  componentDidMount() {
    const { data, reloadChart, status } = this.props
    if ((!data.length && status !== LOADING) || reloadChart) {
      this.props.frontierChart()
    }
  }

  componentDidUpdate(prevProps) {
    const { comparisonItemsStatus, items, detailsData, comparisonItemLoadingSuccess, reloadChart } = this.props
    const noData = chain(items).last().get("noData", false).value()
    const isUnmodeled = chain(items).last().get("isUnmodeled", true).value()
    const latestDataLoaded = !prevProps.detailsData[items.length - 1] && !!detailsData[items.length - 1]
    const dataAlreadyLoaded = !!prevProps.detailsData[items.length - 1] && !!detailsData[items.length - 1]

    if (comparisonItemsStatus === LOADING && (dataAlreadyLoaded || latestDataLoaded || noData || isUnmodeled)) {
      comparisonItemLoadingSuccess()
    }

    if (reloadChart && !prevProps.reloadChart) {
      this.props.frontierChart()
    }
  }

  chartLegendFocus = item => {
    return () => {
      const { chartLegendFocus } = this.props
      chartLegendFocus(item)
    }
  }

  clearChartLegendFocus = () => {
    const { chartLegendFocus } = this.props
    chartLegendFocus(null)
  }

  render() {
    const {
      items,
      detailsItems,
      detailsData,
      selectedGroup,
      groups,
      data,
      status,
      trackPanelTrigger,
      dots,
      hoveredLegendItem,
      toggleFrontierSettings,
      resetSettings,
      setSettings,
      setConstraints,
      currentConstraints,
      removeFactor,
      portfolioData,
      portfolioType,
      frontierSettingsPanelOpen,
      selectedCustomForecast,
      comparisonItemsStatus,
      portfolioNetLong,
      setConstraint,
      setSetting,
      activatedConstraints,
      setFactors,
      removeActivatedFactor,
      setGroup,
      saveGroupToStore,
    } = this.props

    const loadingComponent = <Loading />
    const errorComponent = <Error text="There was an error while loading the efficient frontier. Please try again." />

    return <div className="modules_compare-frontier" elevation={1}>
      <div className="frontier-wrapper">
        {frontierSettingsPanelOpen && <div className="settings-wrapper">
          <OptimizationSettings
            activatedConstraints={activatedConstraints}
            setSetting={setSetting}
            setConstraint={setConstraint}
            setFactors={setFactors}
            removeActivatedFactor={removeActivatedFactor}
            portfolioType={portfolioType}
            resetSettings={resetSettings}
            applyConstraints={setConstraints}
            applySettings={setSettings}
            constraints={currentConstraints}
            removeCurrentFactor={removeFactor}
            portfolioData={portfolioData}
            closeSettings={toggleFrontierSettings}
            portfolioNetLong={portfolioNetLong}
            defaultTab={0}
            reversedActions
            noCollapse
            isOpPro={false}
          />
        </div>}
        <Paper square className="frontier-chart-wrapper">
          {status !== OK && <div className="frontier-settings-button">
            <IconButton
              className="icon"
              color={frontierSettingsPanelOpen ? "primary" : "default"}
              onClick={toggleFrontierSettings}
              size="large"
            >
              <Settings />
            </IconButton>
          </div>}
          {status !== UNKNOWN && <LoadingWrapper status={[status, comparisonItemsStatus]} loadingComponent={loadingComponent} errorComponent={errorComponent}>
            <div className="chart-legend">
              <div className="legend-item-filler">
                <IconButton
                  className="icon"
                  color={frontierSettingsPanelOpen ? "primary" : "default"}
                  onClick={toggleFrontierSettings}
                  size="large"
                >
                  <Settings />
                </IconButton>
              </div>
              {items.map((item, i) =>
                <div
                  className={classNames("legend-item", {
                    blurred: hoveredLegendItem && hoveredLegendItem.id !== item.id,
                  })}
                  onMouseEnter={this.chartLegendFocus(item)}
                  onMouseLeave={this.clearChartLegendFocus}
                  key={i}
                >
                  <div className="legend-dot" style={{ background: item.color }} /> {item.label}
                </div>)}
            </div>
            <div className="chart-wrapper">
              <LineTarget
                data={data}
                height={445}
                dots={dots}
                disabledClick
                xAxisLabel="Predicted Total Risk (%)"
                yAxisLabel={selectedCustomForecast ? "Forecasted Expected Return (%)" : "Implied Expected Return (%)"}
                highlighterLabel="Risk Target"
                focused={hoveredLegendItem}
                TooltipContent={TooltipContent}
              />
            </div>
          </LoadingWrapper>}
        </Paper>
      </div>
      <Paper>
        <CompareFrontierTables
          items={detailsItems}
          data={detailsData}
          columns={items}
          groups={groups}
          selectedGroup={selectedGroup}
          setGroup={setGroup}
          saveGroupToStore={saveGroupToStore}
          storeName="frontier"
          trackPanelTrigger={trackPanelTrigger}
        />
      </Paper>
    </div>
  }
}

export default CompareFrontier
